import React, { useEffect, useRef, useState } from "react";
import { Monitor, Tablet, Smartphone, RotateCw, ExternalLink, Loader2, Sparkles } from "lucide-react";
import { renderUrl } from "../../lib/api";

const DEVICES = [
  ["desktop", Monitor, "100%"],
  ["tablet", Tablet, "768px"],
  ["mobile", Smartphone, "390px"],
];

const Empty = ({ building }) => (
  <div className="h-full flex flex-col items-center justify-center text-center p-8" data-testid="preview-empty">
    <div className="relative w-16 h-16 rounded-2xl border border-emerald-200 bg-emerald-50 flex items-center justify-center">
      {building ? <Loader2 className="animate-spin text-emerald-600" /> : <Sparkles className="text-emerald-600" />}
    </div>
    <h3 className="mt-5 font-display text-lg text-slate-900">{building ? "Building your first version…" : "No preview yet"}</h3>
    <p className="mt-1.5 text-sm text-slate-500 max-w-xs">{building ? "Watch the code being written in the chat. The preview appears here as soon as it's ready." : "Send a message to generate your app."}</p>
  </div>
);

// Listens for runtime errors reported by the in-app SDK of THIS iframe only.
const useFrameErrors = (frameRef, onError, resetKey, onReset) => {
  useEffect(() => { onReset?.(); }, [resetKey]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    const onMsg = (e) => {
      if (!frameRef.current || e.source !== frameRef.current.contentWindow) return;
      const d = e.data;
      if (d && d.__ishukart === "error" && onError) onError({ kind: d.kind, message: d.message, source: d.source, line: d.line, page: d.page });
    };
    window.addEventListener("message", onMsg);
    return () => window.removeEventListener("message", onMsg);
  }, [frameRef, onError]);
};

export const PreviewPane = ({ version, building, banner, errorBanner, onError, onReset }) => {
  const [device, setDevice] = useState("desktop");
  const [page, setPage] = useState("index.html");
  const [nonce, setNonce] = useState(0);
  const frameRef = useRef(null);
  const pages = (version?.files || []).map((f) => f.path).filter((p) => p.endsWith(".html"));
  const frameKey = `${version?.id}-${page}-${nonce}`;
  useFrameErrors(frameRef, onError, frameKey, onReset);

  useEffect(() => { if (!pages.includes(page)) setPage("index.html"); }, [version?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const width = DEVICES.find((d) => d[0] === device)[2];
  const src = version ? renderUrl(version.id, page) : null;

  return (
    <div className="h-full flex flex-col" data-testid="preview-pane">
      <div className="h-11 bg-slate-50 border-b border-slate-200 px-3 flex items-center justify-between gap-3 text-xs font-mono text-slate-500">
        <select value={page} onChange={(e) => setPage(e.target.value)} disabled={!version} data-testid="preview-page-select"
          className="h-7 max-w-[160px] rounded-md bg-slate-50 border border-slate-200 px-2 text-slate-600 outline-none">
          {(pages.length ? pages : ["index.html"]).map((p) => <option key={p} value={p}>{`/${p}`}</option>)}
        </select>
        <div className="flex items-center bg-slate-50 border border-slate-200 rounded-lg p-1 gap-1">
          {DEVICES.map(([id, Icon]) => (
            <button key={id} onClick={() => setDevice(id)} data-testid={`preview-device-${id}`} aria-label={id}
              className={`w-7 h-6 rounded flex items-center justify-center transition-colors ${device === id ? "bg-slate-100 text-emerald-700" : "text-slate-500 hover:text-slate-900"}`}>
              <Icon size={13} />
            </button>
          ))}
        </div>
        <div className="flex items-center gap-1">
          <button onClick={() => setNonce(nonce + 1)} disabled={!version} data-testid="preview-refresh-btn" className="w-7 h-7 rounded flex items-center justify-center hover:text-slate-900 hover:bg-slate-100" aria-label="Refresh"><RotateCw size={13} /></button>
          {src && <a href={src} target="_blank" rel="noreferrer" data-testid="preview-open-new-tab" className="w-7 h-7 rounded flex items-center justify-center hover:text-slate-900 hover:bg-slate-100" aria-label="Open in new tab"><ExternalLink size={13} /></a>}
        </div>
      </div>
      {banner}
      {errorBanner}
      <div className="flex-1 bg-slate-50 flex items-stretch justify-center p-2 sm:p-4 overflow-auto">
        {version ? (
          <div className="h-full transition-[width] duration-300 rounded-lg overflow-hidden border border-slate-200 bg-white shadow-2xl" style={{ width, maxWidth: "100%" }}>
            <iframe
              ref={frameRef}
              key={frameKey}
              title="App preview"
              src={src}
              data-testid="preview-iframe"
              sandbox="allow-scripts allow-forms allow-popups allow-modals allow-downloads"
              className="w-full h-full bg-white"
            />
          </div>
        ) : (
          <Empty building={building} />
        )}
      </div>
    </div>
  );
};

export default PreviewPane;
